import {
  Activity,
  AlertCircle,
  BarChart3,
  Brain,
  Facebook,
  Building2,
  Calendar,
  CalendarDays,
  CheckSquare,
  Workflow,
  Code,
  Cog,
  CreditCard,
  FileCheck,
  FileText,
  Filter,
  FolderOpen,
  Globe,
  History,
  Home,
  Instagram,
  Key,
  ListChecks,
  Lock,
  Layers,
  Megaphone,
  MessageCircle,
  MessageSquare,
  Send,
  Smartphone,
  Crosshair,
  TrendingUp,
  User,
  Users,
  Zap,
  Linkedin,
  Twitter,
  AtSign,
  Youtube,
  Search,
  Shuffle,
  Share2,
  Phone,
  Plug,
  HardDrive,
  Receipt,
  Sparkles,
  Bot,
  LifeBuoy,
  Server,
  GitBranch,
  Percent,
  BookOpen,
  LineChart,
  Wallet,
  FileSpreadsheet,
  Gauge,
  Coins,
  Target,
  Package,
  FileCheck2,
  BellRing,
  Landmark,
  Truck,
  ClipboardList,
  ScrollText,
  Kanban,
  Briefcase,
  ShieldCheck,
  Wand2,
  LayoutDashboard,
  ScanSearch,
  ListOrdered,
  LayoutTemplate,
  FormInput,
  Store,
  FileBarChart,
  PiggyBank,
  Boxes,
  Banknote,
  Settings2,
  Image,
  BrainCircuit,
  Library,
  Network,
  Terminal,
  Puzzle,
  UserCog,
  PhoneCall,
} from 'lucide-react';
import { adsAutomationModules, crmCoreModules, socialChannelModules } from './revopsModules';

/**
 * Top-level sidebar structure: a small set of collapsible parent groups,
 * each with a department icon/accent color and holding one or more labeled
 * sections of nav items. Sales, Social Media, and AI & Support break their
 * items into labeled sub-sections so a dense department doesn't read as one
 * undifferentiated wall of links; Marketing, Finance, and Organization
 * Settings are short enough to stay a single unlabeled section.
 */
export const dashboardGroups = [
  {
    key: 'sales',
    label: 'Sales',
    icon: TrendingUp,
    color: '#0176D3',
    sections: [
      {
        items: [
          { to: '/dashboard/crm', icon: Home, label: 'CRM Dashboard', feature: 'crm_dashboard' },
          { to: '/dashboard/crm/leads', icon: Users, label: 'Leads', feature: 'crm_leads' },
          { to: '/dashboard/crm/deals', icon: Kanban, label: 'Deals & Pipeline', feature: 'crm_deals' },
          { to: '/dashboard/crm/opportunities', icon: Target, label: 'Opportunities', feature: 'crm_opportunities' },
          { to: '/dashboard/crm/sales-dashboard', icon: Gauge, label: 'Sales Dashboard', feature: 'crm_sales_dashboard' },
          { to: '/dashboard/crm/revenue-workspace', icon: Briefcase, label: 'Revenue Workspace', feature: 'crm_revenue_workspace' },
          { to: '/dashboard/analytics', icon: LineChart, label: 'Analytics', feature: 'advanced_analytics' },
        ],
      },
      {
        label: 'Accounts & Contacts',
        items: [
          { to: '/dashboard/crm/accounts', icon: Building2, label: 'Accounts', feature: 'crm_accounts' },
          { to: '/dashboard/crm/contacts', icon: User, label: 'Contacts', feature: 'crm_contacts' },
        ],
      },
      {
        label: 'Routing & Rules',
        items: [
          { to: '/dashboard/crm/departments', icon: GitBranch, label: 'Departments', feature: 'crm_departments' },
          { to: '/dashboard/crm/assignment-rules', icon: Shuffle, label: 'Assignment Rules', feature: 'crm_assignment_rules' },
          { to: '/dashboard/crm/sharing-rules', icon: ShieldCheck, label: 'Sharing Rules', feature: 'crm_sharing_rules' },
        ],
      },
      {
        label: 'Productivity',
        items: [
          { to: '/dashboard/crm/tasks', icon: CheckSquare, label: 'Tasks', feature: 'crm_tasks' },
          { to: '/dashboard/crm/activity-templates', icon: ListChecks, label: 'Activity Templates', feature: 'crm_activity_templates' },
          { to: '/dashboard/crm/activities', icon: Activity, label: 'Recent Activities', feature: 'crm_activities' },
          { to: '/dashboard/crm/events', icon: Calendar, label: 'Calendar Events', feature: 'crm_events' },
          { to: '/dashboard/crm/ai-planner', icon: Wand2, label: 'AI Planner', feature: 'crm_ai_planner' },
          { to: '/dashboard/crm/files', icon: FolderOpen, label: 'Documents & Files', feature: 'crm_files' },
        ],
      },
    ],
  },
  {
    key: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    color: '#FF9900',
    sections: [
      {
        items: [
          { to: '/dashboard/crm/marketing', icon: LayoutDashboard, label: 'Marketing Hub', feature: 'marketing_hub' },
          { to: '/dashboard/crm/lead-scraper', icon: ScanSearch, label: 'Lead Scraper', feature: 'lead_scraper' },
          { to: '/dashboard/enrich', icon: Search, label: 'Lead Enrichment', feature: 'lead_enrichment' },
          { to: '/dashboard/ads', icon: Megaphone, label: 'Ad Campaigns', feature: 'ads_management' },
          { to: '/dashboard/crm/marketing/campaigns', icon: ListOrdered, label: 'Campaign Sequences', feature: 'campaign_sequences' },
          { to: '/dashboard/crm/templates', icon: LayoutTemplate, label: 'Template Builder', feature: 'template_builder' },
          { to: '/dashboard/crm/marketing/forms', icon: Globe, label: 'Lead Capture Forms', feature: 'lead_capture_forms' },
          { to: '/dashboard/forms', icon: FormInput, label: 'Universal Forms', feature: 'universal_forms' },
        ],
      },
    ],
  },
  {
    key: 'finance',
    label: 'Finance',
    icon: Wallet,
    color: '#2E844A',
    sections: [
      {
        items: [
          { to: '/dashboard/crm/finance-dashboard', icon: Gauge, label: 'Finance Dashboard', feature: 'finance_reports' },
          { to: '/dashboard/crm/items', icon: Package, label: 'Items', feature: 'finance_invoices' },
          { to: '/dashboard/crm/quotations', icon: FileSpreadsheet, label: 'Quotations & Orders', feature: 'quotations' },
          { to: '/dashboard/crm/delivery-challans', icon: Truck, label: 'Delivery Challans', feature: 'quotations' },
          { to: '/dashboard/crm/invoices', icon: Receipt, label: 'Customer Invoices', feature: 'finance_invoices' },
          { to: '/dashboard/crm/finance', icon: History, label: 'Payment History', feature: 'finance_payment_history' },
          { to: '/dashboard/crm/collections', icon: BellRing, label: 'Collections', feature: 'finance_invoices' },
          { to: '/dashboard/crm/vendors', icon: Store, label: 'Vendors', feature: 'finance_vendors' },
          { to: '/dashboard/crm/purchase-bills', icon: ClipboardList, label: 'Purchase Bills', feature: 'finance_purchase_bills' },
          { to: '/dashboard/crm/tax-profiles', icon: Percent, label: 'Tax Profiles', feature: 'tax_profiles' },
          { to: '/dashboard/crm/gst-report', icon: FileBarChart, label: 'GST Report', feature: 'gst_report' },
          { to: '/dashboard/crm/compliance', icon: FileCheck2, label: 'GST & TDS Returns', feature: 'gst_report' },
          { to: '/dashboard/crm/banking', icon: Landmark, label: 'Banking', feature: 'bookkeeping' },
          { to: '/dashboard/crm/bookkeeping', icon: BookOpen, label: 'Bookkeeping', feature: 'bookkeeping' },
          { to: '/dashboard/crm/budgets', icon: PiggyBank, label: 'Budgets & Cost Centres', feature: 'bookkeeping' },
          { to: '/dashboard/crm/fixed-assets', icon: Boxes, label: 'Fixed Assets', feature: 'bookkeeping' },
          { to: '/dashboard/crm/expense-claims', icon: Banknote, label: 'Expense Claims', feature: 'bookkeeping' },
          { to: '/dashboard/crm/finance-reports', icon: ScrollText, label: 'Ledgers & Reports', feature: 'finance_reports' },
          { to: '/dashboard/crm/revenue-report', icon: LineChart, label: 'Revenue & Collection Report', feature: 'revenue_report' },
        ],
      },
    ],
  },
  {
    key: 'social',
    label: 'Social Media',
    icon: Share2,
    color: '#7C3AED',
    sections: [
      {
        label: 'Meta WhatsApp',
        items: [
          { to: '/dashboard/meta/chat', icon: MessageSquare, label: 'Live Chat', feature: 'whatsapp_chat' },
          { to: '/dashboard/meta/direct', icon: Send, label: 'Direct Message', feature: 'whatsapp_direct' },
          { to: '/dashboard/meta/campaigns', icon: Megaphone, label: 'Bulk Campaigns', feature: 'bulk_messaging' },
          { to: '/dashboard/meta/automation', icon: Zap, label: 'Automation Rules', feature: 'whatsapp_automation' },
          { to: '/dashboard/meta/templates', icon: LayoutTemplate, label: 'Templates', feature: 'whatsapp_templates' },
          { to: '/dashboard/whatsapp-flows', icon: Layers, label: 'WhatsApp Flows', feature: 'whatsapp_flows' },
          { to: '/dashboard/meta/settings', icon: Settings2, label: 'API Setup', feature: 'whatsapp_settings' },
        ],
      },
      {
        label: 'Instagram',
        items: [
          { to: '/dashboard/instagram/messages', icon: Send, label: 'Direct Messages', feature: 'instagram_dm' },
          { to: '/dashboard/instagram/comments', icon: MessageCircle, label: 'Comments', feature: 'instagram_comments' },
          { to: '/dashboard/instagram/content', icon: Image, label: 'Content', feature: 'content_scheduling' },
          { to: '/dashboard/instagram/automation', icon: Zap, label: 'Automation Rules', feature: 'instagram_automation' },
          { to: '/dashboard/instagram/settings', icon: Settings2, label: 'Connect & Settings', feature: 'instagram_settings' },
        ],
      },
      {
        label: 'Facebook',
        items: [
          { to: '/dashboard/facebook/messages', icon: MessageSquare, label: 'Messages', feature: 'facebook_messages' },
          { to: '/dashboard/facebook/comments', icon: MessageCircle, label: 'Comments', feature: 'facebook_comments' },
          { to: '/dashboard/facebook/automation', icon: Zap, label: 'Automation Rules', feature: 'facebook_automation' },
          { to: '/dashboard/facebook/settings', icon: Settings2, label: 'Connect & Settings', feature: 'facebook_settings' },
        ],
      },
      {
        label: 'Meta Ads',
        items: [
          { to: '/dashboard/meta-ads/campaigns', icon: Megaphone, label: 'Campaigns', feature: 'meta_ads_campaigns' },
          { to: '/dashboard/meta-ads/audiences', icon: Users, label: 'Audiences', feature: 'meta_ads_campaigns' },
          { to: '/dashboard/meta-ads/analytics', icon: BarChart3, label: 'Analytics', feature: 'meta_ads_analytics' },
          { to: '/dashboard/meta-ads/wallet', icon: Wallet, label: 'Account Balance', feature: 'meta_ads_wallet' },
          { to: '/dashboard/meta-ads/settings', icon: Settings2, label: 'Connect & Settings', feature: 'meta_ads_settings' },
        ],
      },
      {
        label: 'Google Ads',
        items: [
          { to: '/dashboard/google-ads/campaigns', icon: Megaphone, label: 'Campaigns', feature: 'google_ads_campaigns' },
          { to: '/dashboard/google-ads/analytics', icon: BarChart3, label: 'Analytics', feature: 'google_ads_analytics' },
          { to: '/dashboard/google-ads/settings', icon: Settings2, label: 'Connect & Settings', feature: 'google_ads_settings' },
        ],
      },
      {
        label: 'LinkedIn',
        items: [
          { to: '/dashboard/linkedin', icon: Linkedin, label: 'LinkedIn', feature: 'linkedin' },
          { to: '/dashboard/linkedin/ai', icon: Sparkles, label: 'AI Skill Center', feature: 'linkedin' },
        ],
      },
      {
        label: 'X (Twitter)',
        items: [
          { to: '/dashboard/x', icon: Twitter, label: 'X (Twitter)', feature: 'x_twitter' },
        ],
      },
      {
        label: 'Threads',
        items: [
          { to: '/dashboard/threads', icon: AtSign, label: 'Threads', feature: 'threads' },
        ],
      },
      {
        label: 'Telegram',
        items: [
          { to: '/dashboard/telegram/messages', icon: Send, label: 'Messages', feature: 'telegram' },
          { to: '/dashboard/telegram/settings', icon: Settings2, label: 'Connect & Settings', feature: 'telegram' },
        ],
      },
      {
        label: 'Cross-Post',
        items: [
          { to: '/dashboard/social/cross-post', icon: Share2, label: 'Cross-Post', feature: 'cross_posting' },
          { to: '/dashboard/social/content-calendar', icon: CalendarDays, label: 'Content Calendar', feature: 'content_calendar' },
        ],
      },
    ],
  },
  {
    key: 'ai-support',
    label: 'AI & Support',
    icon: Bot,
    color: '#EC4899',
    sections: [
      {
        label: 'Support',
        items: [
          { to: '/dashboard/agents', icon: Bot, label: 'Agent Hub', feature: 'agentic_mode', badge: 'agents' },
          { to: '/dashboard/support/widget', icon: Bot, label: 'Support Widget', feature: 'support_widget' },
          { to: '/dashboard/support/tickets', icon: LifeBuoy, label: 'Support Tickets', feature: 'support_tickets' },
          { to: '/dashboard/support/approvals', icon: ShieldCheck, label: 'Agent Approvals', feature: 'support_widget' },
          { to: '/dashboard/support/meter', icon: Gauge, label: 'Agent Meter', feature: 'support_widget' },
          { to: '/dashboard/support/analytics', icon: BarChart3, label: 'Support Analytics', feature: 'support_analytics' },
        ],
      },
      {
        label: 'AI Tools',
        items: [
          { to: '/dashboard/workflow', icon: Workflow, label: 'Workflow Builder', feature: 'workflow_automation' },
          { to: '/dashboard/bot/agentic-flows', icon: BrainCircuit, label: 'Agentic Flows', feature: 'agentic_mode' },
          { to: '/dashboard/bot/knowledge-bases', icon: Library, label: 'Knowledge Base', feature: 'knowledge_base' },
          { to: '/dashboard/voice', icon: Phone, label: 'Voice Calling', feature: 'voice_ai' },
          { to: '/dashboard/support/mcp-connectors', icon: Server, label: 'MCP Connectors', feature: 'mcp_connectors' },
          { to: '/dashboard/support/api-connectors', icon: Network, label: 'API Connectors', feature: 'api_connectors' },
          { to: '/dashboard/support/api-playground', icon: Terminal, label: 'API Playground', feature: 'api_playground' },
          { to: '/dashboard/support/external-modules', icon: Puzzle, label: 'External Modules', feature: 'external_modules' },
          { to: '/dashboard/support/llm-firewall', icon: Lock, label: 'LLM Firewall', feature: 'llm_firewall' },
        ],
      },
    ],
  },
  {
    key: 'org-settings',
    label: 'Org Settings',
    icon: Cog,
    color: '#706E6B',
    sections: [
      {
        label: 'People',
        items: [
          { to: '/dashboard/crm/team', icon: UserCog, label: 'Team & Permissions', feature: 'team_permissions' },
        ],
      },
      {
        label: 'Platform',
        items: [
          { to: '/dashboard/subscription', icon: CreditCard, label: 'Billing & Plans', feature: 'billing' },
          { to: '/dashboard/billing-v2', icon: Wallet, label: 'Billing (New)', feature: 'billing' },
          { to: '/dashboard/billing-v2?tab=api-credits', icon: Coins, label: 'API Credits', feature: 'api_credits' },
          { to: '/dashboard/api-docs', icon: Code, label: 'Developer API', feature: 'api_access' },
          { to: '/dashboard/settings/calling-agent', icon: PhoneCall, label: 'Calling Agent', feature: 'calling_agent_settings' },
          { to: '/dashboard/settings/storage', icon: HardDrive, label: 'Storage', feature: 'storage_settings' },
          { to: '/dashboard/settings/integrations', icon: Plug, label: 'Integrations', feature: 'integrations_settings' },
          { to: '/dashboard/settings', icon: Cog, label: 'General Settings', feature: 'general_settings' },
        ],
      },
    ],
  },
];

export const dashboardWorkspaceModules = [
  { label: 'Revenue Workspace', icon: Home, to: '/dashboard/crm', feature: 'crm_dashboard' },
  { label: 'Lead Generation', icon: TrendingUp, to: '/dashboard/crm/marketing', feature: 'marketing_hub' },
  { label: 'Invoices & Billing', icon: CreditCard, to: '/dashboard/invoices', feature: 'finance_invoices' },
  { label: 'Active Subscription', icon: FileCheck, to: '/dashboard/subscription', feature: 'billing' },
  { label: 'Developer API Docs', icon: Code, to: '/dashboard/api-docs', feature: 'api_access' },
];
